import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { NS } from '@/constants';
import { useAppSelector } from '@/redux';
import { setFooterOptions } from '@/utils/footerUtils';

const ESpot = dynamic(() => import('@/components/layout/espots/ESpot'), { ssr: true });
ESpot.displayName = 'ESpot';
const FooterBottomLinks = dynamic(() => import('@/components/layout/footer/FooterBottomLinks'), { ssr: true });
FooterBottomLinks.displayName = 'FooterBottomLinks';
const FooterHeader = dynamic(() => import('@/components/layout/footer/FooterHeader'), { ssr: true });
FooterHeader.displayName = 'FooterHeader';
const FooterHeaderCheckout = dynamic(() => import('@/components/layout/footer/FooterHeaderCheckout'), { ssr: true });
FooterHeaderCheckout.displayName = 'FooterHeaderCheckout';
const FooterNewsletterForm = dynamic(() => import('@/components/layout/footer/FooterNewsletterForm'), { ssr: true });
FooterNewsletterForm.displayName = 'FooterNewsletterForm';
const FooterSecondDynamicLinksDesktop = dynamic(() => import('@/components/layout/footer/FooterSecondDynamicLinksDesktop'), {
	ssr: true,
});
FooterSecondDynamicLinksDesktop.displayName = 'FooterSecondDynamicLinksDesktop';
const FooterSecondDynamicLinksMobile = dynamic(() => import('@/components/layout/footer/FooterSecondDynamicLinksMobile'), {
	ssr: true,
});
FooterSecondDynamicLinksMobile.displayName = 'FooterSecondDynamicLinksMobile';

import { FOOTER_TYPE } from '@/constants/footer';
import styles from './FooterLayout.module.scss';

const FooterLayout = ({ footerType }) => {
	const { isMobile } = useScreenSizeContext();
	const { t } = useTranslation(NS.COMMON);

	const { storeConf } = useAppSelector(({ root }) => root);
	const { espots } = useAppSelector(({ gui }) => gui);

	const [options, setOptions] = useState(setFooterOptions(footerType));

	useEffect(() => {
		setOptions(setFooterOptions(footerType));
	}, [footerType]);

	return (
		<footer>
			{options.withNewsletter && (
				<div
					className={[styles['container-width'], styles['second-section'], isMobile && styles.mobile]
						.filter(Boolean)
						.join(' ')}
				>
					<FooterNewsletterForm storeId={storeConf.storeId} />
				</div>
			)}
			{options.withFooterHeader && (
				<div className={`${styles['third-section']}${isMobile ? ' ' + styles.mobile : ''}`}>
					<div className={styles['container-width']}>
						<FooterHeader t={t} phone={storeConf.telephone} storeId={storeConf.storeId} />
					</div>
				</div>
			)}
			{options.withFooterHeaderCheckout && (
				<div className={`${styles['third-checkout-section']}${isMobile ? ' ' + styles.mobile : ''}`}>
					<div className={styles['container-width']}>
						<FooterHeaderCheckout storeConf={storeConf} t={t} />
					</div>
				</div>
			)}
			<div className={styles['fourth-section']}>
				<div className={`${styles['container-width']} `}>
					{options.withFooterSecond && (
						<div className={`${styles['footer-second-padding']}${isMobile ? ' ' + styles.mobile : ''}`}>
							{isMobile ? (
								<FooterSecondDynamicLinksMobile t={t} storeConf={storeConf} espots={espots} />
							) : (
								<FooterSecondDynamicLinksDesktop t={t} storeConf={storeConf} espots={espots} />
							)}
						</div>
					)}
					{options.withBottomLink && (
						<div className={`${styles['footer-bottom-padding']}${isMobile ? ' ' + styles.mobile : ''}`}>
							<div className={isMobile ? styles.mobile : null}>
								<FooterBottomLinks links={storeConf.footerLinkBottom} isMobile={isMobile} />
							</div>
						</div>
					)}
				</div>
			</div>
			{options.withLegalNotice && espots?.legalNotice && (
				<div
					className={[
						footerType.includes([FOOTER_TYPE.CHECKOUT, FOOTER_TYPE.PRIMEUR_CHECKOUT])
							? 'container'
							: styles['container-width'],
						styles['fifth-section'],
						isMobile && styles.mobile,
					]
						.filter(Boolean)
						.join(' ')}
				>
					<ESpot spot={espots.legalNotice} />
				</div>
			)}
		</footer>
	);
};

FooterLayout.propTypes = {
	footerType: PropTypes.string,
};

export default FooterLayout;
